import { useCallback, useEffect } from 'react';
import {
    PosMicrosIntegrationMicros4700Config,
    PosMicrosIntegrationTransmissionProtocol,
} from '@mews-commander/data-access';
import { DialogProvider, ModalWrapper, NewModalBodyWrapper, OverlayedLoader } from '@mews-commander/ui';
import { createZodFormResolver, getDefaults, lt, Form } from '@mews/framework';
import { Button, Stack } from '@optimus-web/core';
import { useCloseButton } from '../../../../core/windowManager/hooks';
import ModalHeader from '../../../../legacy/React/Components/Common/ModalHeader';
import {
    PosMicrosConfigurationContextProvider,
    usePosMicrosConfigurationContext,
} from '../contexts/posMicrosConfigurationContext';
import { usePosMicrosConfigurationGet, usePosMicrosConfigurationUpsert } from '../queries/PosMicrosQuery';
import { ConfigurationDetailForm } from './ConfigurationDetailForm';
import { microsConfigurationFormSchema, MicrosConfigurationFormSchema } from './formProps';

const resolver = createZodFormResolver<MicrosConfigurationFormSchema>(microsConfigurationFormSchema);
const microsConfigurationFormDefaultValues: PosMicrosIntegrationMicros4700Config =
    getDefaults(microsConfigurationFormSchema);

const ConfigurationDetailFormContainer = ({ integrationId }: { integrationId: string }) => {
    const { data, isLoading } = usePosMicrosConfigurationGet({ id: integrationId });
    const isEditForm = data && Object.keys(data)?.length > 0;
    const microsConfiguration = data?.configuration;
    const { mutate, isPending: isSaving } = usePosMicrosConfigurationUpsert();
    const { posMicrosConfigurationState, setPosMicrosConfigurationState } = usePosMicrosConfigurationContext();
    const protocol = microsConfiguration?.protocol || PosMicrosIntegrationTransmissionProtocol.MicrosIP;

    useEffect(() => {
        if (microsConfiguration) {
            setPosMicrosConfigurationState((prevState) => ({
                ...prevState,
                microsOutlets: microsConfiguration.microsOutlets ?? [],
            }));
        }
    }, [microsConfiguration, setPosMicrosConfigurationState]);

    const handleSubmit = useCallback(
        (formData: MicrosConfigurationFormSchema) => {
            const posMicrosConfigurationUpsertParams = {
                posMicrosConfiguration: {
                    id: integrationId,
                    configuration: {
                        ...formData,
                        microsOutlets: posMicrosConfigurationState.microsOutlets,
                    },
                },
                createNewConfiguration: !isEditForm,
            };

            mutate(posMicrosConfigurationUpsertParams);
        },
        [integrationId, isEditForm, mutate, posMicrosConfigurationState.microsOutlets]
    );

    return (
        <>
            <OverlayedLoader show={isLoading || isSaving} />
            {!isLoading && (
                <Form
                    onSubmit={handleSubmit}
                    resolver={resolver}
                    defaultValues={microsConfigurationFormDefaultValues}
                    values={microsConfiguration}
                    resetOptions={{ keepDefaultValues: true, keepDirty: true }}
                >
                    {({ submit }) => (
                        <Stack vertical spacing="300">
                            <ConfigurationDetailForm protocol={protocol} />
                            <Stack alignment="end">
                                <Button variant="primary" onClick={submit} type="submit">
                                    {lt('SaveChanges')}
                                </Button>
                            </Stack>
                        </Stack>
                    )}
                </Form>
            )}
        </>
    );
};

export const ConfigurationDetailFormWrapper = ({ integrationId }: { integrationId: string }) => {
    const closeButton = useCloseButton();

    return (
        <PosMicrosConfigurationContextProvider>
            <DialogProvider>
                <ModalWrapper>
                    <ModalHeader title={lt('Configuration')} primaryAction={closeButton} />
                    <NewModalBodyWrapper padded>
                        <ConfigurationDetailFormContainer integrationId={integrationId} />
                    </NewModalBodyWrapper>
                </ModalWrapper>
            </DialogProvider>
        </PosMicrosConfigurationContextProvider>
    );
};
